import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { StyledModal } from '@shoflo/polymer/components/ErrorBoundary/LargeError.styled';
import StyledModalHeader from '@shoflo/polymer/components/Modals/Header.styled';
import { Notification } from '@shoflo/polymer/components/Notification/Notification';
import useIsMobile from 'hooks/useIsMobile';
import { AWAITING_KNOCK_APPROVAL } from 'constants/ModalTypes';
import useKnockRequest from 'hooks/useKnockRequest';
import checkRealtimeSocketConnection from 'actions/CheckRealtimeSocketConnection';
import setKnockFailed from 'actions/Broadcast/setKnockFailed';
import cancelPendingRequest from 'actions/Broadcast/cancelPendingRequest';
import { useGlobalBeforeUnload, useGlobalUnload } from 'hooks/useWindowEvent';
import { StyledModalBody } from './AwaitingKnockApproval.styled';
import DelayedKnockWarning from './DelayedKnockWarning/DelayedKnockWarning';
import { nanoid } from 'nanoid';
import { StudioLogger } from '@studio/logger/StudioLogger';

export const LOG = new StudioLogger('awaiting-knock-approval');

const AwaitingKnockApproval = () => {
  const { modal, realtimeSocketConnected, knockFailed, user } = useSelector(
    ({ modal, realtimeSocketConnected, knockFailed, user }) => ({
      modal,
      realtimeSocketConnected,
      knockFailed,
      user
    })
  );

  const isOpen = modal?.type === AWAITING_KNOCK_APPROVAL;
  const dispatch = useDispatch();

  const { knock } = useSelector(({ knock }) => ({
    knock
  }));
  const knockRejected = knock?.rejected;
  const knockErrored = knock?.errored;

  const isMobile = useIsMobile();
  const knockRequest = useKnockRequest();

  const knockRequestPayload = {
    presenterName: modal?.initialValue,
    isTransient: true,
    presenterDescription: '',
    presenterDetailsUpdated: false,
    sessionId: nanoid(10),
    presenterPhoto: user?.photo
  };

  const handleRetryKnock = () => {
    knockRequest(knockRequestPayload);
  };

  const clearDelayedKnockWarning = () => {
    dispatch(setKnockFailed(false));
  };

  const handlePageReloadOrClose = useCallback(() => {
    dispatch(cancelPendingRequest(knock));
  }, [dispatch, knock]);

  useGlobalBeforeUnload(handlePageReloadOrClose);
  useGlobalUnload(handlePageReloadOrClose);

  useEffect(() => {
    dispatch(checkRealtimeSocketConnection());
  }, [dispatch]);

  useEffect(() => {
    if (knockErrored) {
      LOG.error(
        `presenter ${knockRequestPayload.presenterName} knock request failed`
      );
    }
  }, [knockErrored, knockRequestPayload.presenterName]);

  useEffect(() => {
    if (knockRequestPayload.presenterName && isOpen) {
      LOG.info(
        `Knock request ${
          knockRejected ? 'rejected' : 'awaiting approval'
        } for presenter: ${knockRequestPayload.presenterName}`,
        { presenterName: knockRequestPayload.presenterName }
      );
    }
  }, [isOpen, knockRejected, knockRequestPayload.presenterName]);

  const testid = 'waiting-modal';

  if (!isOpen) return null;

  return (
    <StyledModal
      data-testid={`${testid}`}
      isOpen={isOpen && !knockRejected}
      fade={!isMobile}
      tabIndex={-1}
      aria-labelledby={`${testid}-title`}
      aria-describedby={`${testid}-description`}
    >
      <StyledModalHeader>
        <b id={`${testid}-title`}>Waiting for the host...</b>
      </StyledModalHeader>

      <StyledModalBody id={`${testid}-description`}>
        <p>We're waiting for the host to let you into Studio.</p>
        <br />
        <p>
          <i>
            Note: You will be able to control your mic and camera. The host is
            able to remotely enable your mic and camera if needed.
          </i>
        </p>
        {knockErrored && (
          <Notification
            type="warning"
            onClick={handleRetryKnock}
            message="Failed to send request."
            linkMessage="Try again"
            isDismissible={false}
          />
        )}
        {!realtimeSocketConnected && (
          <Notification
            type="warning"
            message="Realtime connection lost. Attempting to re-connect..."
            isDismissible={false}
          />
        )}
        {knockFailed && (
          <DelayedKnockWarning
            handleRetryKnock={handleRetryKnock}
            clearDelayedKnockWarning={clearDelayedKnockWarning}
          />
        )}
      </StyledModalBody>
    </StyledModal>
  );
};

export default AwaitingKnockApproval;
